import { useState, useEffect, useCallback, useMemo, useRef, memo, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CLOCK_CRITICAL_TIME_MS, getClockLowTimeMs, getClockRearmMs, Color, type BoardState, type Move } from '@draughts/shared';
import { getCapturedByColor } from '../../utils/captures';
import { isDisplayableRating } from '../../utils/rating';
import { drawCountdownFor, frisianCountdownFor } from '../../utils/draw-countdown';
import { titoloApertura } from '../../utils/opening-labels';
import { AlertTriangle, Flag, Check, X, UserX } from 'lucide-react';
import { trackGameResigned, trackDrawOffered, trackDrawAccepted, trackDrawDeclined } from '../../utils/analytics';
import { log } from '../../utils/logger';
import GameBoard from './GameBoard';
import CorniceDamiera from '../shared/CorniceDamiera';
import { useClockTick } from '../../hooks/useClockTick';
import { useExpiryCountdown } from '../../hooks/useExpiryCountdown';
import { useSchermoSveglio } from '../../hooks/useSchermoSveglio';
import { useBloccoLente } from '../../hooks/useBloccoLente';
import type { NetworkClockState } from '../../types/clock';
import { playSound } from '../../utils/audio';
import { vibrate } from '../../utils/vibration';
import { BUTTON_REVEAL_DELAY_MS } from '../../constants/timing';
import { GameHeader } from './GameHeader';
import { PlayerCard } from './PlayerCard';
import { UserAvatar } from './UserAvatar';
import { AmbientEffects } from './AmbientEffects';
import { OrologioContext, type ValoriOrologio } from './orologio-contesto';
import { GameToolbar } from './GameToolbar';
import OfferNotice from './OfferNotice';
import DrawCountdownSlot from './DrawCountdown';
import InGameSettingsModal from './InGameSettingsModal';
import { FullscreenPanel } from './FullscreenPanel';
import ReviewControls from './ReviewControls';
import PlayerProfileOverlay from '../profile/PlayerProfileOverlay';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useFocusStore } from '../../store/focus-store';
import { useBlindStore } from '../../store/blind-store';
import BlindPeekButton from './BlindPeekButton';
import BlindMoveAnnouncement from './BlindMoveAnnouncement';
import { useBlindMoveAnnouncement } from '../../hooks/useBlindMoveAnnouncement';
import BlindLastMoveButton from './BlindLastMoveButton';
import BoardScaleControl from './BoardScaleControl';
import { getStoredBoardScale, setStoredBoardScale, stepBoardScale } from '../../utils/board-scale';
import BarraSaluto, { useSaluto } from './SalutoBuonGioco';
import { BlindGameContext } from './blind-context';
import './GameScreen.css';

// ====== Types ======

export interface MatchFoundData {
  opponentUsername: string;
  /** Assente o `null` per chi non ha punteggio. */
  opponentRating?: number | null;
  opponentAvatar?: string | null;
  assignedColor: 'white' | 'black';
  countdownSeconds?: number; // default 3
  /**
   * Forced opening (tournament Italian variant). `id` + `forza` arrivano gia' dal
   * server (`tournament:match`), e vanno a schermo insieme: «APERTURA N° 56 (1N)»
   * e' quello che l'arbitro annuncia, e la coppia e' cio' che identifica la riga
   * del Regolamento. Solo quando la pescata viene davvero da una tabella: il
   * filtro e' dentro `titoloApertura` (vedi `ballotNumber`).
   */
  forcedOpening?: { id?: number; moves: string[]; forza?: string };
}

export interface PlayerInfo {
  username: string;
  /** Assente o `null` per chi non ha punteggio (ospiti e bot). */
  rating?: number | null;
  avatar?: string | null;
  country?: string | null;
  /**
   * Real account username, when this player has an account. Kept separate from
   * `username` on purpose: that field is a display label and is often a
   * localized literal ("You", "Anonymous", "Computer (easy)"). Containers opt in
   * explicitly, so a new one can't accidentally link to a bot's profile.
   */
  profileUsername?: string | null;
  /** Un segno subito dopo il nome, per tutta la partita (il ⚡ del boost). */
  nameBadge?: ReactNode;
}

/**
 * A panel (Moves, Chat, …) that lives in the desktop sidebar but, on mobile,
 * is reached through a button in the in-game bar and opened as a full-screen
 * overlay. `render` is called lazily so the content only mounts while open.
 */
export interface MobilePanel {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Unread/notification count shown as a badge on the trigger button. */
  badge?: number;
  /**
   * Trigger di sola icona: l'etichetta diventa `aria-label` invece di essere
   * scritta sotto. A 360px la barra è già piena — frecce di revisione, «Mosse» e
   * «Chat» — e una terza parola non ci sta: si strizzerebbe fino ad andare a
   * capo dentro il bottone. Il titolo del pannello aperto resta `label`.
   */
  compact?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  render: () => ReactNode;
}

export interface GameScreenProps {
  // Board state
  boardState: unknown; // BoardState synced to game store externally
  playerColor: 'white' | 'black';
  isMyTurn: boolean;
  isGameOver: boolean;
  /**
   * Si stanno sfogliando le mosse: la damiera mostra una posizione PASSATA.
   *
   * Serve perche' la card dell'avversario si accende PER NEGAZIONE
   * (`!isMyTurn`), e `isMyTurn` nella partita locale e' falso anche durante la
   * revisione — cosi' il cartellino di sopra si prendeva un bordo verde che non
   * gli spettava, e con la scritta «Muove» sarebbe diventata una bugia a parole
   * mentre il tratto e' mio. Mentre si sfoglia non si accende NESSUNA delle due:
   * «a chi tocca» e' una domanda sospesa, non una con una risposta.
   */
  isReviewing?: boolean;
  flipped: boolean;
  onMove: (move: Move) => void;
  /** Allow queuing a pre-move during the opponent's turn. */
  allowPremove?: boolean;

  // Clock
  clockState: NetworkClockState | null;
  isPlaying: boolean;
  /**
   * Si puo' toccare la damiera?
   *
   * Separata da `isMyTurn` perche' rispondono a due domande diverse: «di chi e'
   * il turno» lo dice la posizione, «si puo' giocare» lo dice la partita. Il
   * turno da solo non basta: `boardState.status` resta `PLAYING` anche a
   * partita finita, quindi una damiera restava afferrabile con la partita
   * chiusa, il socket giu' o lo stato incoerente dopo un rientro — pezzi che si
   * selezionano e si trascinano mentre nessuna mossa arriva da nessuna parte.
   *
   * Il valore predefinito `true` tiene fuori la partita locale, dove non esiste
   * un socket da perdere: li' basta `!isGameOver`.
   */
  boardInteractive?: boolean;
  tickClock?: () => void;

  // Player info
  topPlayer: PlayerInfo;
  bottomPlayer: PlayerInfo;

  // Turn/result display
  turnText: string;
  resultClass?: string; // 'win' | 'loss' | 'draw' | ''

  // Grace period (opponent disconnected)
  isOpponentDisconnected?: boolean;
  graceTimeRemaining?: number | null;
  /**
   * Il giocatore IN BASSO e' caduto. Serve solo a chi guarda (`readOnly`): chi
   * gioca e' sempre lui in basso, e la propria caduta non la vede. La grazia e'
   * la stessa `graceTimeRemaining`: cade uno alla volta.
   */
  isBottomPlayerDisconnected?: boolean;
  /** L'avversario non si e' mai presentato (torneo). Vedi `AvvisoAvversario`. */
  opponentMaiArrivato?: boolean;
  /** Se non arriva o non rientra, si vince quando scade il SUO orologio. */
  opponentDecideLOrologio?: boolean;

  /**
   * Scadenza della PRIMA mossa vera (epoch ms), o null quando non c'è — in
   * torneo, dove l'orologio parte subito, o a mossa già giocata.
   *
   * Il conto finisce sulla card di chi ha il tratto, quindi lo vedono tutt'e
   * due i giocatori nello stesso posto: chi deve muovere come urgenza, chi
   * aspetta come spiegazione.
   */
  primaMossaScadeAt?: number | null;
  primaMossaEsito?: 'void' | 'forfeit' | null;
  /**
   * Quante semi-mosse ha messo il sorteggio. Due consumatori, e vanno tenuti
   * insieme:
   *   - sapere se una mossa VERA è già stata giocata
   *     (`moveHistory.length > openingPlyCount`), che è la stessa condizione con
   *     cui il server chiude la finestra della prima mossa;
   *   - il PAVIMENTO di «vai all'inizio» nelle frecce di revisione: l'inizio della
   *     partita è la posizione dopo l'apertura imposta, non la damiera nuda.
   *
   * Il secondo è il motivo per cui ora la passa anche chi non ha la finestra della
   * prima mossa (la partita locale, chi guarda): deve essere LO STESSO numero che
   * il contenitore dà alla colonna laterale, o il bottone atterrerebbe in due punti
   * diversi secondo la larghezza dello schermo.
   */
  openingPlyCount?: number;

  // Match found overlay (board hidden during countdown)
  matchFoundData?: MatchFoundData | null;
  onMatchCountdownEnd?: () => void;

  // Local mode (AI game - board uses internal store)
  isLocalMode?: boolean;
  /**
   * Partita in presenza: due persone vere a questo dispositivo.
   *
   * Scollega l'unico consumo di `isMyTurn` che in presenza mente. La damiera deve
   * accettare ENTRAMBI i colori, ma le schede giocatore e gli effetti d'ambiente
   * devono continuare a illuminarne una sola — quella di chi ha il tratto. Chi
   * chiama passa quindi il valore onesto in `isMyTurn` ("tocca a chi sta in basso")
   * e questo flag apre la damiera anche all'altro.
   */
  hotSeat?: boolean;
  /** Chiede conferma prima della patta (in presenza chiude subito la partita). */
  confirmDraw?: boolean;

  // Slots
  /* Celle in più dentro la riga del GameHeader (il tempo che resta in arena, il
     nome e il turno in torneo). Qui e non in una striscia a parte, che e' quel
     che c'era: una striscia sta FUORI dalla griglia, e la sua altezza la paga la
     damiera — vedi il commento su `celleTorneo` in TournamentGame.tsx. */
  headerCells?: ReactNode;
  overlaySlot?: ReactNode;
  /** Overlay rendered inside the board wrapper (absolute-positioned over the board). */
  boardOverlaySlot?: ReactNode;
  actionsSlot?: ReactNode;
  /** Dentro la barra dei controlli, al posto di Focus: scelte brevi che
   *  spariscono una volta prese (il boost d'arena). Passalo solo quando va
   *  mostrato — quando manca, Focus torna al suo posto. */
  focusSlot?: ReactNode;
  sidebarSlot?: ReactNode;
  /** Mobile-only: panels opened as full-screen overlays from the in-game bar.
   *  On desktop the sidebar (`sidebarSlot`) is used instead. */
  mobilePanels?: MobilePanel[];
  /**
   * Mobile: chiude il pannello aperto ogni volta che CAMBIA — chi lo passa
   * usa l'inizio di una partita nuova. Chi stava scrivendo in chat quando parte
   * il ritorno non deve ritrovarsi l'orologio che corre sotto un pannello a
   * tutto schermo.
   */
  closePanelsKey?: number | null;

  // Draw offer
  onOfferDraw?: () => void;
  onAcceptDraw?: () => void;
  onDeclineDraw?: () => void;
  drawOffered?: boolean;
  opponentOfferedDraw?: boolean;
  /**
   * Messaggio transitorio sull'esito di una proposta fatta all'avversario
   * ("ha rifiutato la patta"), mostrato in una pastiglia sopra la barra.
   * Chi decide quando sparisce e' il chiamante, che possiede il flag di store.
   */
  offerNotice?: string | null;
  /** Il freno alle proposte di patta: vedi `GameToolbar`. Puro inoltro. */
  drawBlockedLabel?: string | null;
  drawBlockedReason?: string | null;

  // Takeback (casual online only). The board fires the request/rated-blocked on a
  // pickup during review; the toolbar shows the opponent's incoming request.
  // `takebackAllowed` is the RAW casual flag (room.isRated === false), NOT the
  // display `isRated` (which also folds in guest/bot opponents).
  takebackAllowed?: boolean;
  onTakebackRequest?: (toPly: number) => void;
  onRatedTakebackBlocked?: () => void;
  opponentRequestedTakeback?: boolean;
  pendingTakebackPlies?: number | null;
  onAcceptTakeback?: () => void;
  onDeclineTakeback?: () => void;

  // Actions
  onResign?: () => void;
  onLeave?: () => void;
  /** Gira la damiera: solo per chi guarda (`readOnly`). Vedi GameToolbar. */
  onFlipBoard?: () => void;

  // Game metadata (for new layout header)
  variant?: 'international' | 'italian' | 'english';
  timeControlLabel?: string;
  /** Cadence key (e.g. `'bullet-1+0'`); sets when the low-time warning fires. */
  timeControlPreset?: string | null;
  isRated?: boolean;
  /** Win-condition modifier (normal/breakthrough/antidraughts); shown as a header badge. */
  ruleMode?: string;
  /**
   * The viewer is watching someone else's game, not playing it.
   *
   * Note what this does NOT do: `isMyTurn` keeps its real meaning. It drives
   * both board interactivity AND the turn indicator on the player cards, and a
   * spectator still needs to see whose move it is — passing `false` would leave
   * the top card permanently lit. So `readOnly` neutralises the board and the
   * player-only controls, and leaves the turn display alone.
   */
  readOnly?: boolean;
}

// ====== Match Found Overlay (internal) ======

function MatchFoundOverlay({
  data,
  onCountdownEnd,
}: {
  data: MatchFoundData;
  onCountdownEnd: () => void;
}) {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(data.countdownSeconds ?? 3);
  const [showButton, setShowButton] = useState(false);
  /* Numero di tabella e forza, dal compositore condiviso: e' lo stesso titolo del
     cartellino sulla damiera e della lista mosse. Il filtro sulle pescate senza
     tabella dietro sta la' dentro, e non piu' in un `numeroApertura` di questo file. */
  const titolo = titoloApertura(t, data.forcedOpening);

  // Sound + haptic on mount + delayed button reveal
  useEffect(() => {
    /*
     * Qui NON si suona. Il suono d'inizio partita sta nei punti d'avvio dei
     * quattro store, perche' questo overlay e' solo uno dei modi in cui una
     * partita comincia: l'arena lo salta (`consumaPartitaInCoda`) e lo svizzero
     * pure, e li' la partita cominciava in silenzio. Qui resta il vibrato, che
     * accompagna l'apparire della scheda.
     */
    log.info('GameScreen', 'Match-found overlay', { opponent: data.opponentUsername, color: data.assignedColor });
    vibrate([200, 100, 200]);
    const timer = setTimeout(() => setShowButton(true), BUTTON_REVEAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) {
      log.info('GameScreen', 'Match countdown ended');
      onCountdownEnd();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((c) => c - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, onCountdownEnd]);

  const handleStartNow = useCallback(() => {
    onCountdownEnd();
  }, [onCountdownEnd]);

  return (
    <div className="gs-match-overlay" data-testid="match-found-overlay">
      <div className="gs-match-card">
        <h2 className="gs-match-title">
          {t('arena.matchFound.title', 'Match Found!')}
        </h2>

        <div className="gs-match-opponent">
          <span className="gs-match-label">
            {t('arena.matchFound.opponent', 'Opponent')}
          </span>
          <UserAvatar username={data.opponentUsername} avatar={data.opponentAvatar} variant="opponent" />
          <span className="gs-match-name" data-testid="match-opponent-name">
            {data.opponentUsername}
          </span>
          {/* La guardia di casa, come nel cartellino: uno 0 non e' un punteggio. */}
          {isDisplayableRating(data.opponentRating) && (
            <span className="gs-match-rating" data-testid="match-opponent-rating">
              ({data.opponentRating})
            </span>
          )}
        </div>

        <div className="gs-match-color">
          <span className={`gs-match-color-dot ${data.assignedColor}`} />
          <span data-testid="match-assigned-color">
            {data.assignedColor === 'white'
              ? t('arena.matchFound.playAsWhite', 'Playing as White')
              : t('arena.matchFound.playAsBlack', 'Playing as Black')}
          </span>
        </div>

        {data.forcedOpening && (
          <div className="gs-match-opening">
            <span className="gs-match-opening-label">
              {titolo?.titolo}
              {titolo?.codice && <span className="gs-match-opening-forza"> ({titolo.codice})</span>}
            </span>
            <span className="gs-match-opening-moves">
              {data.forcedOpening.moves.join(', ')}
            </span>
          </div>
        )}

        <div className="gs-match-countdown">
          <span className="gs-match-countdown-label">
            {t('arena.matchFound.startingIn', 'Starting in')}
          </span>
          <span className="gs-match-countdown-number" data-testid="match-countdown">
            {countdown}
          </span>
        </div>

        {showButton && (
          <button
            className="gs-match-start-btn"
            onClick={handleStartNow}
            type="button"
            data-testid="match-start-now-btn"
          >
            {t('arena.matchFound.startNow', 'Start Now')}
          </button>
        )}
      </div>
    </div>
  );
}

// ====== GameScreen ======

/**
 * Le prop dell'albero di partita, meno l'orologio grezzo e piu' i suoi derivati.
 *
 * `clockState` e `tickClock` restano fuori di proposito: sono gli unici due
 * valori che cambiano identita' quattro volte al secondo, e finche' erano qui
 * nessun `memo` poteva fermarsi. Li tiene l'involucro in fondo al file.
 */
type PropsInterne = Omit<GameScreenProps, 'clockState' | 'tickClock'> & {
  hasClock: boolean;
  isMyClockActive: boolean;
  isOppClockActive: boolean;
  isMyClockLow: boolean;
  isOppClockLow: boolean;
};

function GameScreenInterno({
  boardState,
  playerColor,
  isMyTurn,
  isGameOver,
  isReviewing = false,
  flipped,
  onMove,
  allowPremove = false,
  hasClock,
  isMyClockActive,
  isOppClockActive,
  isMyClockLow,
  isOppClockLow,
  isPlaying,
  boardInteractive = true,
  topPlayer,
  bottomPlayer,
  turnText,
  resultClass = '',
  isOpponentDisconnected = false,
  graceTimeRemaining = null,
  isBottomPlayerDisconnected = false,
  opponentMaiArrivato = false,
  opponentDecideLOrologio = false,
  primaMossaScadeAt = null,
  primaMossaEsito = null,
  openingPlyCount = 0,
  matchFoundData,
  onMatchCountdownEnd,
  isLocalMode = false,
  hotSeat = false,
  confirmDraw = false,
  headerCells,
  boardOverlaySlot,
  overlaySlot,
  actionsSlot,
  focusSlot,
  sidebarSlot,
  mobilePanels,
  closePanelsKey = null,
  onOfferDraw,
  onAcceptDraw,
  onDeclineDraw,
  drawOffered = false,
  opponentOfferedDraw = false,
  offerNotice = null,
  drawBlockedLabel = null,
  drawBlockedReason = null,
  takebackAllowed = false,
  onTakebackRequest,
  onRatedTakebackBlocked,
  opponentRequestedTakeback = false,
  pendingTakebackPlies = null,
  onAcceptTakeback,
  onDeclineTakeback,
  onResign,
  onLeave,
  onFlipBoard,
  variant,
  timeControlLabel,
  timeControlPreset,
  isRated,
  ruleMode,
  readOnly = false,
}: PropsInterne) {
  const { t } = useTranslation();
  // Mobile (≤899px) swaps the desktop sidebar for full-screen Moves/Chat
  // overlays opened from the in-game bar, keeping the game screen scroll-free.
  const isMobile = useIsMobile();
  const panels = mobilePanels ?? [];
  const [openPanelId, setOpenPanelId] = useState<string | null>(null);
  // Profile of the player whose name was tapped, or null. An overlay rather
  // than a screen change: App.tsx force-navigates back here while the game is
  // in progress, and this way the board and clock stay alive underneath.
  const [profileFor, setProfileFor] = useState<string | null>(null);
  const closeProfile = useCallback(() => { setProfileFor(null); }, []);
  /* LA MISURA DEL GRUPPO «nome + damiera + nome», da 900px in su.
     Stato di schermata e non uno store: lo si ricostruisce da localStorage a ogni
     montaggio, che e' anche cio' che lo fa sopravvivere a un ricarico e alla
     partita dopo. Non va nelle preferenze dell'account di proposito — vedi la
     testata di `utils/board-scale.ts`. */
  const [scalaDamiera, setScalaDamiera] = useState(getStoredBoardScale);
  const cambiaScalaDamiera = useCallback((verso: 1 | -1) => {
    setScalaDamiera((precedente) => {
      const prossima = stepBoardScale(precedente, verso);
      setStoredBoardScale(prossima);
      return prossima;
    });
  }, []);
  const openPanel = panels.find((p) => p.id === openPanelId) ?? null;
  const handleOpenPanel = useCallback((panel: MobilePanel) => {
    panel.onOpen?.();
    setOpenPanelId(panel.id);
  }, []);
  const handleClosePanel = useCallback(() => {
    setOpenPanelId((current) => {
      panels.find((p) => p.id === current)?.onClose?.();
      return null;
    });
  }, [panels]);

  useEffect(() => {
    if (closePanelsKey === null) return;
    handleClosePanel();
    // Solo al cambio della chiave: `handleClosePanel` cambia coi pannelli.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closePanelsKey]);

  // The mobile bar hosts the "Moves" trigger at the left, before the review
  // arrows; any other panels (e.g. Chat) render to its right.
  const movesPanel = panels.find((p) => p.id === 'moves') ?? null;
  const otherPanels = panels.filter((p) => p.id !== 'moves');
  const renderMobileBarButton = (panel: MobilePanel) => (
    <button
      key={panel.id}
      type="button"
      className={`gs-mobile-bar-btn${panel.compact ? ' gs-mobile-bar-btn--icon' : ''}`}
      aria-label={panel.compact ? panel.label : undefined}
      onClick={() => handleOpenPanel(panel)}
    >
      {panel.icon}
      {panel.compact ? null : <span>{panel.label}</span>}
      {panel.badge ? <span className="gs-mobile-bar-badge">{panel.badge}</span> : null}
    </button>
  );

  // On mobile the desktop sidebar (which marks the chat tab active/inactive)
  // isn't mounted, so establish the "closed" baseline ourselves whenever no
  // overlay is open — the chat then accumulates an unread badge until opened.
  useEffect(() => {
    if (!isMobile || openPanelId !== null) return;
    panels.forEach((p) => p.onClose?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, openPanelId]);

  // Log game started on mount
  useEffect(() => {
    log.info('GameScreen', 'Game started', {
      mode: isLocalMode ? 'ai' : 'multiplayer',
      topPlayer: topPlayer.username,
      bottomPlayer: bottomPlayer.username,
      playerColor,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Log game over
  useEffect(() => {
    if (isGameOver) {
      log.info('GameScreen', 'Game ended', { result: resultClass, turnText });
    }
  }, [isGameOver, resultClass, turnText]);

  // Log opponent disconnect / reconnect
  useEffect(() => {
    if (isOpponentDisconnected) {
      log.warn('GameScreen', 'Opponent disconnected');
    } else {
      // Only log reconnect if we had a grace period active
      if (graceTimeRemaining && graceTimeRemaining > 0) {
        log.info('GameScreen', 'Opponent reconnected');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpponentDisconnected]);

  // Log grace period
  useEffect(() => {
    if (graceTimeRemaining && graceTimeRemaining > 0) {
      log.info('GameScreen', 'Grace period started', { graceTimeMs: graceTimeRemaining });
    } else if (graceTimeRemaining === 0) {
      log.info('GameScreen', 'Grace period ended');
    }
  }, [graceTimeRemaining]);

  // Grace period countdown (local tick)
  const [displayGraceTime, setDisplayGraceTime] = useState<number | null>(null);
  const graceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (graceIntervalRef.current) {
      clearInterval(graceIntervalRef.current);
      graceIntervalRef.current = null;
    }

    if (!graceTimeRemaining || graceTimeRemaining <= 0) {
      setDisplayGraceTime(null);
      return;
    }

    setDisplayGraceTime(graceTimeRemaining);

    /*
     * Il conto alla rovescia si MISURA, non si decrementa a passo fisso.
     *
     * `prev - 1000` a ogni tick presume che `setInterval` scatti ogni mille
     * millisecondi esatti, e non è così: in secondo piano i browser lo strozzano a
     * un tick al minuto (o lo fermano del tutto), e su 1 vCPU sotto carico slitta
     * comunque. Il risultato era l'unico orologio dell'app che non misura il tempo
     * reale: chi tornava in primo piano vedeva il grace period a 25 secondi mentre
     * sul server era già scaduto — e la sconfitta arrivava con un conto ancora in
     * corso a schermo.
     *
     * Ora si tiene l'ISTANTE di scadenza e si ricalcola il residuo a ogni tick:
     * un tick perso non sposta il valore mostrato, lo salta. Il tick resta a un
     * secondo perché è la granularità con cui si legge, non la fonte del valore.
     */
    const scadenza = Date.now() + graceTimeRemaining;

    graceIntervalRef.current = setInterval(() => {
      const residuo = Math.max(0, scadenza - Date.now());
      setDisplayGraceTime(residuo);
      if (residuo <= 0 && graceIntervalRef.current) {
        clearInterval(graceIntervalRef.current);
        graceIntervalRef.current = null;
      }
    }, 1000);

    return () => {
      if (graceIntervalRef.current) clearInterval(graceIntervalRef.current);
    };
  }, [graceTimeRemaining]);

  // Toggle gs-ambient class on body for wood grain + vignette
  useEffect(() => {
    document.body.classList.add('gs-ambient');
    return () => {
      document.body.classList.remove('gs-ambient');
    };
  }, []);

  // In-game settings modal (graphics/game preferences)
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  /**
   * Stabile, per la stessa ragione di `OnlineGame`: `GameScreen` si ri-renderizza a ogni
   * tick dell'orologio, e una arrow inline farebbe fallire il `memo` di `GameToolbar`.
   */
  const apriImpostazioni = useCallback(() => setShowSettingsModal(true), []);

  // ALLA CIECA. La benda la porta chi GIOCA, e solo finché si gioca:
  //  - a partita finita la scacchiera si rivela, o non ci sarebbe modo di rivedere
  //    quello che si è appena giocato a memoria;
  //  - chi guarda da fuori (`readOnly`) non è bendato: non sta giocando, e una
  //    scacchiera vuota non è niente da guardare.
  // Durante la partita la benda regge anche in review: tornare indietro di una mossa
  // mostrerebbe altrimenti tutta la posizione, che è esattamente ciò che si sta
  // cercando di tenere a mente. Da quando i controlli di revisione spariscono alla
  // cieca — e le righe della lista mosse non sono più toccabili — quella condizione non
  // è più raggiungibile giocando: la benda resta come rete, non come regola d'uso.
  //
  // Il valore viaggia anche nel contesto (`BlindGameContext`, più sotto): è da lì che lo
  // leggono i pezzi di schermo che non sono figli diretti di questo file — la lista
  // mosse nella colonna laterale e quella nel pannello a schermo intero.
  const blindGame = ruleMode === 'blind' && !isGameOver && !readOnly;

  // Focus mode (zen) — a persistent toggle held in a store so it survives the
  // GameScreen remounts of a rematch / new game (see focus-store.ts).
  const isFocusOn = useFocusStore((s) => s.isFocusOn);
  const toggleFocus = useFocusStore((s) => s.toggleFocus);
  // The zen visual treatment applies only while a game is in progress, and only
  // to someone playing it. At game over the toggle stays ON (so it carries into
  // the next game) but its effects are suspended, letting the full review view
  // return — the Moves/Chat bar, header and player info that focus otherwise
  // hides. La notazione delle caselle NON e' fra quelle: e' una preferenza
  // esplicita dell'utente (`boardNotation`, spenta per default), quindi chi l'ha
  // accesa la vede anche in Focus — zen toglie il contorno, non cambia la damiera.
  // `readOnly` is suspended the same way, and for a stronger reason: a spectator
  // has no Focus button to switch it back off (see GameToolbar), and zen hides
  // every other toolbar button — including the Leave that is their only way out.
  // The flag itself is left alone so watching someone else's game doesn't
  // silently reset the player's own preference.
  // Alla cieca zen e' sospeso allo stesso modo, e per la stessa ragione del `readOnly`
  // portata all'estremo: dalla partita bendata NON si esce da zen, perche' il posto del
  // bottone Focus lo occupa «Sbircia» (`focusSlot`, piu' sotto) e zen nasconde tutti gli
  // altri bottoni della barra. Misurato in Chromium: con Focus rimasto acceso da una
  // partita precedente — il flag sopravvive apposta al cambio partita — una partita alla
  // cieca si apriva con ZERO bottoni in barra e senza la barra delle mosse, cioe' senza
  // comandi e senza l'unica informazione che un giocatore bendato ha. Anche nel merito
  // non ha senso: zen toglie le distrazioni per lasciare la damiera, e bendati la
  // damiera e' vuota. Il flag resta acceso: la prossima partita normale lo ritrova.
  const zenActive = isFocusOn && !isGameOver && !readOnly && !blindGame;

  const peekUntilMs = useBlindStore((s) => s.peekUntilMs);
  const endPeek = useBlindStore((s) => s.endPeek);
  const isPeeking = blindGame && peekUntilMs !== null;
  // Il timer si arma sulla SCADENZA, non sulla durata: una seconda sbirciata mentre la
  // prima è accesa sposta la scadenza e questo effetto si riarma, invece di lasciare in
  // giro il timer vecchio che spegnerebbe la scacchiera prima del tempo.
  useEffect(() => {
    if (peekUntilMs === null) return;
    const remaining = peekUntilMs - Date.now();
    if (remaining <= 0) { endPeek(); return; }
    const timer = setTimeout(endPeek, remaining);
    return () => clearTimeout(timer);
  }, [peekUntilMs, endPeek]);

  // L'annuncio dell'ultima mossa: bendati è ciò che sostituisce la voce dell'arbitro.
  // Chiamato UNA volta sola e passato a tutti e due i posti che lo mostrano — l'alone
  // sulle caselle e il banner con la notazione — così non possono divergere.
  const announcement = useBlindMoveAnnouncement(blindGame);
  useEffect(() => {
    if (!zenActive) return;
    document.body.classList.add('zen');
    return () => {
      document.body.classList.remove('zen');
    };
  }, [zenActive]);

  // Derived display values for new GameScreen layout
  const variantLabel = (() => {
    const v = (variant ?? 'international').toLowerCase();
    const fallback = v.charAt(0).toUpperCase() + v.slice(1);
    return t(`game.variants.${v}Short`, { defaultValue: fallback });
  })();

  const timeLabel = timeControlLabel ?? '—';
  const isRatedGame = Boolean(isRated);

  // Captured pieces per side, derived from the canonical move history.
  // Each card shows the OPPOSITE-color pieces that player has taken.
  // Memoized because the clock re-renders this component several times a second:
  // without it both scans of the move history run on every tick, and a fresh
  // array identity would defeat PlayerCard's memo on its `captured` prop anyway.
  const moveHistory = useMemo(
    () =>
      boardState && typeof boardState === 'object' && 'moveHistory' in boardState
        ? ((boardState as BoardState).moveHistory ?? [])
        : [],
    [boardState]
  );
  /*
   * Quanto manca alla patta automatica. DUE regole, una riga: il limite di mosse
   * (`halfMoveClock` contro `drawMoveLimit`) e le 7 mosse frisoni di due dame
   * contro una (`frisianEndgamePly`). Entrambi i contatori li mantiene gia' il
   * core e viaggiano interi nel payload: qui si leggono e basta.
   *
   * Il `??` NON e' una precedenza da ricordare: le due regole sono mutuamente
   * esclusive su tutte le 27 varianti della matrice (frisone e Frysk hanno
   * `drawMoveLimit: 0`; nessun'altra ha `frisianEndgameDraws`), e
   * `variants-spec.test.ts` sorveglia l'invariante. Al massimo una delle due
   * torna un valore, quindi l'ordine e' leggibilita', non logica.
   *
   * Torna `null` in ogni caso in cui non c'e' niente da mostrare — variante
   * ignota (i test passano `boardState: {}`), variante senza nessuna delle due
   * regole, o frisone non ancora in 2-dame-contro-1.
   */
  const drawCountdown = useMemo(() => {
    if (!boardState || typeof boardState !== 'object') return null;
    const b = boardState as Partial<BoardState>;
    return (
      frisianCountdownFor(b.variant, b.frisianEndgamePly, b.moveHistory?.length ?? 0) ??
      drawCountdownFor(b.variant, b.halfMoveClock)
    );
  }, [boardState]);

  /*
   * Il conto della PRIMA mossa.
   *
   * La visibilità si DERIVA dalla damiera invece di vivere in un flag da
   * spegnere: `moveHistory.length <= openingPlyCount` è esattamente la
   * condizione con cui il server chiude la finestra
   * (`getPlayedMoveCount() === 0`). Un flag avrebbe avuto tre cicli di vita da
   * tenere allineati — la delega all'arena, quella al torneo, e la mossa
   * ottimistica locale che precede l'eco del server — e sarebbe bastato
   * sbagliarne uno per lasciare il conto acceso su una partita già cominciata.
   */
  /*
   * La frase per chi aspetta. Solo a chi gioca: allo spettatore basta la scheda.
   * Dopo lo zero della grazia normale non si dice niente: la fine della partita
   * sta arrivando dal server, e una frase sui secondi a zero sarebbe falsa.
   */
  const avvisoConSecondi =
    isOpponentDisconnected && !opponentMaiArrivato && !opponentDecideLOrologio;
  const avvisoAvversario: string | null = (() => {
    if (!isOpponentDisconnected || isGameOver || readOnly) return null;
    /* Se tocca a chi legge, il SUO orologio corre e quello dell'altro no:
       «vinci allo scadere del suo orologio» senza «muovi tu» lo lascerebbe
       fermo a guardare il proprio tempo che se ne va. Col sorteggio
       dell'apertura il primo tratto e' spesso del Nero, quindi e' il caso
       frequente, non quello raro. */
    if (opponentMaiArrivato) {
      return t(isMyTurn ? 'game.disconnection.avvisoMaiArrivatoTuoTurno' : 'game.disconnection.avvisoMaiArrivato');
    }
    if (opponentDecideLOrologio) {
      return t(isMyTurn ? 'game.disconnection.avvisoDecideOrologioTuoTurno' : 'game.disconnection.avvisoDecideOrologio');
    }
    if (displayGraceTime != null && displayGraceTime > 0) {
      return t('game.disconnection.avvisoGrazia', { secondi: Math.ceil(displayGraceTime / 1000) });
    }
    return null;
  })();

  const primaMossaAncoraDaGiocare =
    !isGameOver && primaMossaScadeAt !== null && moveHistory.length <= openingPlyCount;
  /*
   * Il saluto d'apertura: `null` quando la barra deve restare quella dei comandi.
   *
   * Sta qui e non dentro un componente che decide da se' perche' la risposta serve
   * a CHI MONTA LA BARRA — o il saluto, o `GameToolbar` — ed e' la stessa ragione
   * (e lo stesso stampo) di `useCanBoost` in ArenaBoostButton.
   *
   * `sospeso` e' la regola di precedenza: una cortesia cede il passo a chi sta
   * chiedendo una decisione (patta, rifai-mossa), all'esito che parla nello stesso
   * punto e alla scheda «avversario trovato», che coprirebbe il saluto mentre i suoi
   * secondi scorrono.
   */
  const saluto = useSaluto({
    boardState,
    playerColor,
    openingPlyCount,
    abilitato: !readOnly && !isLocalMode && !hotSeat && !isGameOver,
    sospeso:
      Boolean(matchFoundData) ||
      Boolean(opponentOfferedDraw) ||
      Boolean(drawOffered) ||
      opponentRequestedTakeback ||
      offerNotice !== null,
  });

  const secondiPrimaMossa = useExpiryCountdown(
    primaMossaAncoraDaGiocare ? primaMossaScadeAt : null
  );
  /*
   * Si mostra solo nell'ULTIMO MINUTO. Con un'apertura sorteggiata a cadenza
   * lunga la scadenza può valere mezz'ora: mostrarla per intero metterebbe un
   * secondo orologio accanto a quello vero, e direbbe «urgenza» dove non ce
   * n'è. A mossa libera la scadenza è 30s, quindi si vede da subito — che è il
   * caso comune.
   */
  const attesaPrimaMossaSec =
    primaMossaAncoraDaGiocare && secondiPrimaMossa <= 60 ? secondiPrimaMossa : null;
  /** Tocca al Bianco? Decide su QUALE delle due card finisce il conto. */
  const attesaSulBianco = useMemo(() => {
    if (!boardState || typeof boardState !== 'object') return true;
    return (boardState as Partial<BoardState>).currentTurn !== Color.BLACK;
  }, [boardState]);
  const attesaInBasso = attesaSulBianco === (playerColor === 'white');

  const capturedByBottom = useMemo(
    () => getCapturedByColor(moveHistory, playerColor === 'white' ? Color.BLACK : Color.WHITE),
    [moveHistory, playerColor]
  );
  const capturedByTop = useMemo(
    () => getCapturedByColor(moveHistory, playerColor === 'white' ? Color.WHITE : Color.BLACK),
    [moveHistory, playerColor]
  );
  const advantageBottom = capturedByBottom.length - capturedByTop.length;
  const advantageTop = capturedByTop.length - capturedByBottom.length;


  // Once the game is over the clock is gone; show the outcome in its slot.
  // resultClass is from the bottom player's (self) perspective; the opponent's
  // is the mirror (win↔loss, draw stays draw).
  const overResult =
    isGameOver && (resultClass === 'win' || resultClass === 'loss' || resultClass === 'draw')
      ? (resultClass as 'win' | 'loss' | 'draw')
      : null;
  const myResult = overResult;
  const oppResult = overResult === 'win' ? 'loss' : overResult === 'loss' ? 'win' : overResult;

  /*
   * LA BARRA COMANDI, montata in due posti a seconda della larghezza.
   *
   * Su desktop va nel piede della colonna destra: i suoi ~60px erano il 9% del
   * viewport di un portatile, e la damiera prende quel che avanza. Sotto i 900px
   * la colonna destra non c'e' e la barra resta in fondo a quella della damiera.
   *
   * NON LA SOSTITUISCE PIU' NESSUNO. Per un periodo il saluto d'apertura le
   * prendeva il posto, e per i suoi secondi sparivano Abbandona, Patta e
   * Impostazioni: i comandi non sono una cosa che si mette via per far parlare una
   * cortesia. Il saluto ora ha il suo posto — vedi `salutoBarra` qui sotto.
   */
  const barraComandi = (!isGameOver || readOnly) ? (
    <GameToolbar
      onResign={onResign ?? (() => {})}
      onOfferDraw={onOfferDraw ?? (() => {})}
      onAcceptDraw={onAcceptDraw}
      onDeclineDraw={onDeclineDraw}
      drawOffered={Boolean(drawOffered)}
      opponentOfferedDraw={Boolean(opponentOfferedDraw)}
      opponentRequestedTakeback={opponentRequestedTakeback}
      pendingTakebackPlies={pendingTakebackPlies}
      onAcceptTakeback={onAcceptTakeback}
      onDeclineTakeback={onDeclineTakeback}
      onToggleFocus={toggleFocus}
      isFocusOn={isFocusOn}
      /* Scelta che si prende una volta sola e in fretta (il boost d'arena): sta
         al posto di Focus, dove le mani sono già, e gli restituisce il posto
         appena è presa o scaduta.
         Alla cieca «Sbircia» prende il posto di Focus — la barra regge quattro
         posti a 360px. È anche il posto giusto nel merito: zen nasconde la lista
         mosse, che bendati è l'unico appiglio rimasto. */
      focusSlot={!isGameOver ? (blindGame ? <BlindPeekButton /> : focusSlot) : undefined}
      onOpenSettings={apriImpostazioni}
      confirmDraw={confirmDraw}
      /* In presenza quel comando non consegna la partita a nessuno: la chiude.
         Vedi `senzaVincitore` in GameToolbar. */
      senzaVincitore={hotSeat}
      drawBlockedLabel={drawBlockedLabel}
      drawBlockedReason={drawBlockedReason}
      readOnly={readOnly}
      // Only a read-only viewer gets a Leave button here; a player
      // leaves through Resign or the app's own navigation.
      onLeave={readOnly ? onLeave : undefined}
      // Idem per «Gira»: a chi gioca la damiera la orienta il suo colore.
      onFlipBoard={readOnly ? onFlipBoard : undefined}
    />
  ) : null;

  /*
   * IL SALUTO HA UN POSTO SUO, e non e' quello dei comandi.
   *
   * Sotto i 900px prende il posto della BARRA DEI PANNELLI (Mosse / frecce /
   * Chat): e' li' che si puo' togliere qualcosa per qualche secondo senza
   * conseguenze — a inizio partita non c'e' niente da rivedere, e il saluto
   * stesso E' l'evento di chat. Da 900px in su quella barra non esiste, e il
   * saluto va nella colonna destra sopra i comandi (`.gs-sidebar-sopra-barra`).
   */
  const salutoBarra = saluto ? <BarraSaluto {...saluto} /> : null;

  return (
    /* SOPRA LA GRIGLIA NON VA NIENTE, e `headerSlot` non esiste piu'.
       Era l'unico modo di metterci qualcosa, e lo usava solo il torneo: la sua
       striscia era `flex-shrink: 0` sopra un `.gs-grid` che prende `flex: 1`,
       quindi i suoi ~73px li pagava la damiera — in torneo era piu' piccola che
       in una partita normale. Ora quelle informazioni sono celle della riga
       dell'intestazione (`headerCells`), come l'arena ha fatto prima. Lo slot e'
       sparito e non lasciato inerte di proposito: era la porta da cui il difetto
       sarebbe rientrato. */
    /* Il flag «si sta giocando alla cieca» raggiunge così anche chi non è figlio
       diretto di questo file: la colonna laterale (uno slot che costruiscono i
       contenitori) e il pannello a schermo intero (un portale, che conserva la
       posizione nell'albero React). */
    <BlindGameContext.Provider value={blindGame}>
    <div className={`game-screen${isGameOver ? ' gs-over' : ''}`}>
      <AmbientEffects
        isMyTurn={isMyTurn && !isGameOver}
        isLowTime={isMyClockLow && !isGameOver}
      />

      <div className="gs-grid">
        {/* La variabile del regolo sta sulla COLONNA e non sulla banda: la banda e'
            il contenitore di dimensione, e un valore che si eredita da fuori non
            rischia di sembrare una cosa che il contenitore interroga su se stesso.
            Scritta solo da 900px in su: sotto non c'e' il regolo, e il ripiego `1`
            delle formule in GameScreen.css e' la damiera di sempre. */}
        <div
          className="gs-board-column"
          style={isMobile ? undefined : ({ '--gs-scala-damiera': scalaDamiera } as CSSProperties)}
        >
          {/* `spectating` E' `readOnly`: questa prop significa gia' «chi e' qui
              guarda, non gioca» (vedi GameToolbar) e oggi ha un solo chiamante,
              SpectateGame. Chi un giorno riusasse `readOnly` per una damiera non
              guardata da uno spettatore si porterebbe dietro anche il badge. */}
          {/* L'intestazione sta qui solo sotto i 900px. Sopra, va nella testa della
              colonna destra: i suoi 34px piu' lo spazio erano altezza tolta alla
              damiera, e di lato non costano niente. */}
          {isMobile && (
            <GameHeader variant={variant ?? 'international'} variantLabel={variantLabel} timeLabel={timeLabel} rated={isRatedGame} ruleMode={ruleMode} extraCells={headerCells} spectating={readOnly} />
          )}

          {/*
            * Cosa succede se l'avversario non c'e'. La scheda dice «Disconnesso»
            * o «Non presente», ma non la conseguenza: il 28 settembre due
            * giocatori hanno guardato un orologio scendere per sette minuti,
            * ricaricando la pagina per capire se la partita fosse viva.
            *
            * Una riga sua e non la pastiglia sopra la barra: quella e' per gli
            * esiti di pochi secondi, e questa puo' restare minuti — sopra la
            * barra coprirebbe la scheda e l'orologio di chi legge. Su telefono
            * la damiera e' limitata dalla larghezza, quindi la riga prende
            * l'aria verticale che avanza invece di stringerla.
            */}
          {avvisoAvversario && (
            /* Regione «live» solo per le frasi ferme: con i secondi dentro, il
               lettore di schermo la ripeterebbe a ogni secondo. */
            <div
              className="gs-avviso-avversario"
              role={avvisoConSecondi ? undefined : 'status'}
              data-testid="avviso-avversario"
            >
              <UserX size={16} aria-hidden="true" />
              <span>{avvisoAvversario}</span>
            </div>
          )}

          {/* La banda centrale: cartellino avversario, damiera, cartellino proprio,
              tenuti insieme. E' LEI la riga elastica della colonna e il contenitore di
              dimensione, quindi la damiera e' il quadrato piu' grande che ci sta — e
              quando avanza altezza (la damiera e' quasi sempre limitata dalla LARGHEZZA)
              l'aria la distribuisce `justify-content` SOPRA e SOTTO il gruppo. Prima i
              cartellini erano fratelli del vano e quell'aria cadeva FRA cartellino e
              damiera, lasciandoli appiccicati all'intestazione e alla barra comandi. */}
          <div className="gs-middle">
            <PlayerCard
              username={topPlayer.username}
              rating={topPlayer.rating ?? null}
              avatar={topPlayer.avatar}
              country={topPlayer.country}
              variant="opponent"
              pieceColor={playerColor === 'white' ? 'black' : 'white'}
              hasClock={hasClock}
              latoOrologio="avversario"
              isClockActive={isOppClockActive}
              isClockLow={isOppClockLow}
              isMyTurn={!isMyTurn && !isGameOver && !isReviewing}
              readOnly={readOnly}
              isOpponentDisconnected={isOpponentDisconnected}
              graceTimeRemaining={displayGraceTime ?? null}
              avversarioMaiArrivato={opponentMaiArrivato}
              attesaPrimaMossaSec={attesaInBasso ? null : attesaPrimaMossaSec}
              attesaPrimaMossaEsito={primaMossaEsito}
              captured={capturedByTop}
              advantage={advantageTop}
              result={oppResult}
              /* In presenza la card di sopra e' sempre quella di chi sta dall'altro
                 lato del tavolo — anche dopo un «Gira», che ribalta la damiera e
                 scambia il CONTENUTO delle due card ma non il «sopra» e il «sotto».
                 Quindi la rotazione non dipende dal colore: dipende dal posto. */
              ruotata={hotSeat}
              profileUsername={topPlayer.profileUsername}
              nameBadge={topPlayer.nameBadge}
              onProfileClick={setProfileFor}
            />

            {/* L'overlay dell'apertura sorteggiata resta DENTRO il wrapper: e' in
                position:absolute ancorato a lui, che e' il rettangolo della damiera e
                non quello della banda. */}
            <CorniceDamiera
              className="gs-board-wrapper"
              classeInterna="gs-board-frame"
              overlay={boardOverlaySlot}
              /* Il regolo sta a fianco della cornice, non sopra la damiera, ed e'
                 solo da 900px in su: sotto la damiera e' limitata dalla larghezza
                 (336px a 360 di viewport) e non c'e' niente da regolare.
                 In focus non si MONTA, e non e' la stessa cosa di nasconderlo:
                 il vano che gli spetta e i due tetti allargati stanno su
                 `:has(.gs-regolo)`, che guarda l'albero e non il disegno —
                 nascosto, il focus si ritroverebbe 52px di niente al posto della
                 damiera. */
              fianco={isMobile || zenActive ? undefined : (
                <BoardScaleControl value={scalaDamiera} onStep={cambiaScalaDamiera} />
              )}
            >
                <GameBoard
                  isOnlineMode={!isLocalMode}
                  onOnlineMove={onMove}
                  // A spectator's board is inert: GameBoard's pointer handler
                  // returns early on !isMyTurn, and with premoves off there is no
                  // queue-an-intent path left either.
                  isMyTurn={readOnly ? false : (hotSeat ? !isGameOver : isMyTurn && !isGameOver && boardInteractive)}
                  /* Non e' `!isMyTurn`: quello e' il turno dell'avversario, dove
                     la pre-mossa e' viva. Qui la partita non e' giocabile. */
                  inerte={!readOnly && (isGameOver || !boardInteractive)}
                  /* Anche la pre-mossa passa di qui: il suo unico sbarramento in
                     `GameBoard` e' `boardState.status`, che a partita finita e'
                     ancora `PLAYING`. Senza questo si continuerebbe ad accodare
                     intenzioni su una partita che non c'e' piu'. */
                  allowPremove={allowPremove && !readOnly && !isGameOver && boardInteractive}
                  flipped={flipped}
                  onlinePlayerColor={playerColor}
                  takebackAllowed={takebackAllowed}
                  onTakebackRequest={onTakebackRequest}
                  onRatedTakebackBlocked={onRatedTakebackBlocked}
                  blind={blindGame}
                  peeking={isPeeking}
                  revealLastMove={announcement.visible}
                />
            </CorniceDamiera>

            <PlayerCard
              username={bottomPlayer.username}
              rating={bottomPlayer.rating ?? null}
              avatar={bottomPlayer.avatar}
              country={bottomPlayer.country}
              variant="self"
              /* L'annuncio della mossa prende il posto di «Tocca a te» per i suoi cinque
                 secondi: la riga ha gia' lo spazio riservato, quindi non allunga la
                 schermata, e non copre piu' la damiera come faceva da sopra. */
              statusNote={announcement.visible ? <BlindMoveAnnouncement move={announcement.move} /> : undefined}
              pieceColor={playerColor}
              hasClock={hasClock}
              latoOrologio="mio"
              isClockActive={isMyClockActive}
              isClockLow={isMyClockLow}
              isMyTurn={isMyTurn && !isGameOver}
              readOnly={readOnly}
              /* Come la card di sopra: niente filtro su `isGameOver`, o a partita
                 finita le due card direbbero cose diverse dello stesso fatto. */
              isOpponentDisconnected={readOnly && isBottomPlayerDisconnected}
              graceTimeRemaining={readOnly && isBottomPlayerDisconnected ? displayGraceTime ?? null : null}
              attesaPrimaMossaSec={attesaInBasso ? attesaPrimaMossaSec : null}
              attesaPrimaMossaEsito={primaMossaEsito}
              captured={capturedByBottom}
              advantage={advantageBottom}
              result={myResult}
              profileUsername={bottomPlayer.profileUsername}
              nameBadge={bottomPlayer.nameBadge}
              onProfileClick={setProfileFor}
            />
          </div>

          {/* A partita finita al giocatore la barra viene sostituita da
              `actionsSlot` (rivincita, uscita). Chi guarda un `actionsSlot` non
              ce l'ha: togliergli la barra vorrebbe dire lasciarlo su una
              damiera ferma senza uscita e senza impostazioni.

              La pastiglia dell'esito e' in position:absolute ancorata al wrapper:
              sta sopra la barra senza occupare altezza, e questo e' l'unico punto
              che partita online, arena e torneo condividono. Il wrapper e' un item
              di .gs-board-column (flex column): senza `flex: 0 0 auto` nel CSS la
              barra entrerebbe nella stretta verticale insieme alla damiera. */}
          {/* LA PASTIGLIA DELL'ESITO RESTA ATTACCATA ALLA DAMIERA, la barra comandi no.
              `OfferNotice` e' `position: absolute` con `bottom: calc(100% + N)`:
              si alza dal suo ancoraggio. Finche' l'ancoraggio era la barra comandi
              stava sopra la barra, cioe' sotto la damiera — che e' dove va letta.
              Da quando su desktop la barra se ne va nella colonna destra (che per
              giunta e' `overflow: hidden`) le due cose si separano: la pastiglia
              resta qui in fondo alla colonna della damiera, la barra viaggia.
              QUI DENTRO C'ERA ANCHE IL CONTO DELLE N MOSSE, e si vedeva male: alto
              zero piu' `bottom: calc(100% + 4px)` lo metteva a quattro px dal fondo
              di `.gs-middle`, e dove la damiera e' limitata dall'ALTEZZA (il
              portatile, il telefono basso o in orizzontale) quella banda e' piena al
              pixel — la riga finiva SOPRA il cartellino di chi gioca. Ora e' una
              riga nel flusso, subito sopra la barra comandi, dove sta il pulsante
              che la riguarda: vedi piu' giu'.
              L'ANCORAGGIO e' sempre montato (alto zero, non costa niente). */}
          <div className="gs-notice-anchor">
            {readOnly && (
              /* Chi guarda non ha il modal di fine partita (copriva la posizione
                 finale): il motivo della fine lo legge qui, in una riga nel flusso.
                 Montata VUOTA per tutta la partita e riempita alla fine: un lettore
                 di schermo annuncia una regione `status` quando cambia, non quando
                 nasce gia' piena. Vuota non occupa spazio (`:empty` nel CSS). */
              <p className="gs-esito-spettatore" role="status" data-testid="gs-esito-spettatore">
                {isGameOver ? turnText : ''}
              </p>
            )}
            {(!isGameOver || readOnly) && <OfferNotice message={offerNotice} />}
          </div>

          {/* Sotto i 900px la barra comandi resta dov'e' sempre stata: la colonna
              destra non esiste. Sopra, se ne va nel suo piede — vedi `barraComandi`.
              Il conto verso la patta le sta SOPRA, nel flusso: e' la riga che
              spiega il pulsante Patta, e qui non puo' coprire niente. Costa 16px di
              colonna solo da quando si accende, e solo dove la damiera e' limitata
              dall'altezza — che e' esattamente il caso in cui prima finiva sul
              cartellino (misurato a 360x640: damiera da 288 a 272). */}
          {isMobile && barraComandi && (
            <div className="gs-toolbar-anchor">
              <DrawCountdownSlot
                value={drawCountdown}
                drawOffered={Boolean(drawOffered)}
                opponentOfferedDraw={Boolean(opponentOfferedDraw)}
              />
              {barraComandi}
            </div>
          )}

          {/* IL PANNELLO DI FINE PARTITA FA LA STESSA STRADA DELLA BARRA COMANDI, di
              cui prende il posto: sotto i 900px resta qui, da 900px in su se ne va in
              fondo alla colonna destra (`.gs-sidebar-azioni`, piu' giu').
              Prima stava qui a ogni larghezza, e su desktop era un figlio che la
              colonna della damiera guadagnava PROPRIO a partita finita: `.gs-middle`
              e' `flex: 1 1 0` e la damiera si misura in `cq*` sul suo riquadro,
              quindi la posizione finale si rimpiccioliva nel momento esatto in cui
              era la cosa da guardare. */}
          {isMobile && isGameOver && actionsSlot && (
            <div className="gs-actions-slot">{actionsSlot}</div>
          )}

          {/* IL SALUTO PRENDE IL POSTO DI QUESTA BARRA, non di quella dei comandi.
              Le due hanno la stessa statura, e ORA e' vero: qui c'era scritto «52px
              di bottone», ma 52 e' il `min-height` del BOTTONE e il contenuto lo
              supera — le due misuravano 61 e 54, e i 7px di differenza li pagava la
              damiera a ogni cambio. Oggi leggono tutt'e due `--gs-barra-h`
              (GameScreen.css), che da quei pezzi e' derivato.
              Il prezzo, dichiarato: per i suoi secondi
              spariscono frecce, «Mosse» e «Chat» col badge dei non letti — a inizio
              partita non c'e' niente da rivedere, e il saluto stesso E' l'evento di
              chat. Non e' legato a `panels.length`: dove i pannelli non ci sono il
              saluto si rende lo stesso. */}
          {isMobile && (salutoBarra ?? (panels.length > 0 && (
            <div className="gs-mobile-bar">
              {/* Alla cieca le frecce non hanno niente da mostrare — sotto la benda
                  la posizione rivista è vuota quanto quella corrente — e online fanno
                  danno: entrare in revisione blocca la damiera, e l'uscita era proprio
                  l'«avanti-tutta». Al loro posto va l'unica cosa che bendati conta:
                  l'ultima mossa, scritta grande. Apre comunque la lista completa, che è
                  dov'era il bottone «Mosse».
                  NB: in quel ramo `centerSlot` non si passa MAI — ReviewControls alla
                  cieca restituisce null e si porterebbe via anche il trigger «Mosse»,
                  che è ospitato lì dentro.
                  Fuori dalla cieca: il trigger «Mosse» sta a sinistra, prima delle
                  frecce; gli altri pannelli (la Chat) seguono a destra. */}
              {blindGame ? (
                movesPanel && <BlindLastMoveButton onOpen={() => handleOpenPanel(movesPanel)} />
              ) : (
                <ReviewControls
                  variant="bar"
                  centerSlot={movesPanel ? renderMobileBarButton(movesPanel) : undefined}
                  openingPlyCount={openingPlyCount}
                />
              )}
              {otherPanels.map(renderMobileBarButton)}
            </div>
          )))}
        </div>

        {/* LA COLONNA DESTRA, in quattro pezzi.
            La condizione era `!isMobile && sidebarSlot`: ora che qui dentro vivono
            anche l'intestazione e la BARRA COMANDI, legarla allo slot vorrebbe
            dire che un contenitore senza colonna si porterebbe via l'unico modo
            di abbandonare la partita o aprire le impostazioni. Oggi lo slot lo
            passano tutti e cinque i contenitori, quindi e' una guardia contro il
            futuro — ma e' il genere di guardia che si scrive prima, non dopo.
            I pezzi sono separati perche' il focus li tratta in modo diverso: ne
            nasconde due (testa e corpo), fa galleggiare di lato il piede e manda in
            basso al centro quello dei messaggi. Vedi `body.zen .gs-sidebar` in
            GameScreen.css. */}
        {!isMobile && (
          <div className="gs-sidebar">
            <div className="gs-sidebar-head">
              <GameHeader variant={variant ?? 'international'} variantLabel={variantLabel} timeLabel={timeLabel} rated={isRatedGame} ruleMode={ruleMode} extraCells={headerCells} spectating={readOnly} colonna />
            </div>
            {sidebarSlot && <div className="gs-sidebar-body">{sidebarSlot}</div>}
            {/* SOPRA I COMANDI, NON DENTRO. Qui vivono il saluto e il conto verso la
                patta: due cose che si leggono, e che il piede invece non puo'
                ospitare. La faccia «saluto ricevuto» porta la classe
                `game-toolbar--draw-offer` (indossa il vestito dell'offerta), e in
                zen c'e' una regola `.gs-sidebar-foot:has(.game-toolbar--draw-offer)`
                che butta il piede in basso al centro: con il saluto li' dentro si
                sarebbe portata dietro l'intera barra comandi per cinque secondi.
                Il contenitore si rende sempre e si toglie da solo quando e' vuoto
                (`:empty` nel CSS), cosi' non si prende uno spazio della colonna. */}
            {barraComandi && (
              <>
                <div className="gs-sidebar-sopra-barra">
                  {salutoBarra}
                  <DrawCountdownSlot
                    value={drawCountdown}
                    drawOffered={Boolean(drawOffered)}
                    opponentOfferedDraw={Boolean(opponentOfferedDraw)}
                  />
                </div>
                <div className="gs-sidebar-foot">{barraComandi}</div>
              </>
            )}

            {/* IL PANNELLO DI FINE PARTITA, in fondo alla colonna: prende il posto
                della barra comandi, ed e' giusto che lo prenda anche qui invece di
                restare sotto la damiera (vedi la nota al suo innesto da telefono).

                NON SI ESCLUDONO PER COSTRUZIONE, e va bene cosi': `barraComandi` e'
                `null` esattamente quando `isGameOver && !readOnly`, quindi a chi
                gioca i due piedi non convivono mai. A chi GUARDA la barra resta, ma
                nessun contenitore gli passa un `actionsSlot`; se un giorno glielo
                passasse, i due si impilano in ordine leggibile invece di scomparire.

                LA CLASSE NON E' `.gs-sidebar-foot`, ed e' igiene piu' che necessita':
                `zenActive` esclude `isGameOver`, quindi a partita finita `body.zen`
                non c'e' e le regole che fanno galleggiare il piede non possono
                toccare questo contenuto. Ma quel nome oggi significa «il piede della
                barra comandi» per quattro regole dello zen e per tre test che lo
                cercano cosi': un secondo elemento con la stessa classe renderebbe
                ambiguo il loro `querySelector`. `.gs-actions-slot` resta per il
                vestito (colonna flex), `.gs-sidebar-azioni` aggiunge quel che e'
                della colonna. */}
            {isGameOver && actionsSlot && (
              <div className="gs-actions-slot gs-sidebar-azioni">{actionsSlot}</div>
            )}
          </div>
        )}
      </div>

      {matchFoundData && (
        <MatchFoundOverlay
          data={matchFoundData}
          onCountdownEnd={onMatchCountdownEnd ?? (() => {})}
        />
      )}

      {overlaySlot}

      {openPanel && (
        <FullscreenPanel title={openPanel.label} onClose={handleClosePanel}>
          {openPanel.render()}
        </FullscreenPanel>
      )}

      {profileFor && (
        <PlayerProfileOverlay username={profileFor} onClose={closeProfile} />
      )}

      <InGameSettingsModal open={showSettingsModal} onClose={() => setShowSettingsModal(false)} />
    </div>
    </BlindGameContext.Provider>
  );
}

const GameScreenMemo = memo(GameScreenInterno);

/**
 * L'INVOLUCRO CHE TIENE L'OROLOGIO, perche' l'albero sotto non debba piu' saperlo.
 *
 * Qui dentro si paga il tick — quattro volte al secondo — e si paga poco: questo
 * componente non rende una schermata, rende DUE elementi. Sotto, `memo` ferma la
 * riconciliazione di tutto il resto finche' nessuno dei booleani cambia, e quelli
 * cambiano solo quando cambia il turno o si attraversa una soglia.
 *
 * Il valore grezzo scende per contesto fino ai soli quadranti (vedi
 * `orologio-contesto.tsx`, che porta i numeri della misura). I derivati scendono
 * per prop, ed e' questa la divisione che fa funzionare la cosa: se i millisecondi
 * fossero una prop, `memo` non potrebbe fermarsi mai.
 *
 * ⚠️ NON AGGIUNGERE QUI UNA PROP CHE CAMBI A OGNI TICK, e non spostare quaggiu'
 * uno stato che cambi con la stessa cadenza: tornerebbe tutto com'era, e senza
 * che niente si rompa a vista.
 */
export default function GameScreen(props: GameScreenProps) {
  const { clockState, tickClock, isPlaying, playerColor, isGameOver, readOnly, timeControlPreset } = props;

  /* La lente (il pinch a molla di `useLente`) qui non deve fare nulla: la
     scacchiera e' gia' dimensionata sulla viewport, non c'e' un dettaglio
     piccolo che l'ingrandimento sveli, e un secondo dito che fa saltare la
     vista mentre si trascina un pezzo e' solo un modo per sbagliare mossa.

     Sta nell'INVOLUCRO e non piu' dentro, per due ragioni che coincidono:
     l'involucro e' montato esattamente quando lo e' l'albero di partita, e non
     ha uscite anticipate — quindi l'invariante che
     `useBloccoLente.cablaggio.test.ts` sorveglia («mai sotto un `return`») qui
     e' vera per costruzione invece che per attenzione. */
  useBloccoLente();

  /*
   * E lo schermo resta acceso finche' la partita non e' finita.
   *
   * Qui, nell'involucro, per la stessa ragione della riga sopra: e' montato
   * esattamente quando lo e' l'albero di partita, e copre in un colpo tutte e
   * cinque le schermate che lo usano — locale, online, torneo, arena e
   * spettatore.
   *
   * `!isGameOver` e non «l'orologio corre»: le partite SENZA orologio esistono
   * (la sfida al computer, la partita in presenza a tempo «nessuno») e sono
   * proprio quelle in cui il telefono sta appoggiato e nessuno lo tocca per
   * minuti. Legarlo all'orologio avrebbe lasciato a secco l'unico caso che
   * questo risolve davvero.
   */
  useSchermoSveglio(!isGameOver);

  // L'orologio a schermo e' `PlayerClock` (con `useSmoothClock`). Il vecchio
  // `NetworkGameClock`, che aveva un intervallo suo, era codice morto.
  useClockTick(clockState, isPlaying, tickClock);

  const hasClock = clockState !== null;
  const myClockMs = hasClock ? (playerColor === 'white' ? clockState.white : clockState.black) : null;
  const oppClockMs = hasClock ? (playerColor === 'white' ? clockState.black : clockState.white) : null;
  const isMyClockActive = isPlaying && clockState?.activePlayer === playerColor;
  const isOppClockActive = Boolean(
    isPlaying && clockState?.activePlayer && clockState.activePlayer !== playerColor
  );
  // The warning threshold follows the cadence (bullet 10s … classical 60s), with
  // a second one near zero so a 60s warning isn't followed by a minute of silence.
  // In bullet the two coincide, and `criticalMs < lowMs` keeps it to one warning.
  const lowMs = getClockLowTimeMs(timeControlPreset);
  const criticalMs = Math.min(CLOCK_CRITICAL_TIME_MS, lowMs);
  const isMyClockLow = myClockMs !== null && myClockMs <= lowMs && myClockMs > 0;
  const isOppClockLow = oppClockMs !== null && oppClockMs <= lowMs && oppClockMs > 0;
  const isMyClockCritical =
    criticalMs < lowMs && myClockMs !== null && myClockMs <= criticalMs && myClockMs > 0;

  // Hysteresis, on the alert only — the red styling above and the ambient glow
  // below still follow `isMyClockLow` exactly. Re-arming the moment the clock
  // recovered made an increment cadence beep once per MOVE: the increment lands
  // the clock a hair over the threshold the instant you move, and the next tick
  // back under it fires again. The alert now re-arms only past the re-arm band.
  // Reduced to booleans on purpose: `myClockMs` changes every tick and must
  // never reach a dependency array — these flip only on a band crossing.
  // A null clock counts as recovered, which is what re-arms the alerts between
  // games: every store clears `clockState` at game end, so a rematch on this
  // same mounted GameScreen always starts with clean guards.
  const lowRearmMs = getClockRearmMs(lowMs, timeControlPreset);
  const criticalRearmMs = getClockRearmMs(criticalMs, timeControlPreset);
  const isAboveLowRearm = myClockMs === null || myClockMs > lowRearmMs;
  const isAboveCriticalRearm = myClockMs === null || myClockMs > criticalRearmMs;

  // Buzz and beep once when the player's own clock first crosses each threshold,
  // matching the short pulse used for piece moves.
  // playSound gates on the mute preference internally.
  const lowTimeFiredRef = useRef(false);
  const criticalTimeFiredRef = useRef(false);
  // `readOnly` suppresses both: the "my clock" these watch belongs to a player
  // the viewer is merely watching, so buzzing their phone would be nonsense.
  useEffect(() => {
    if (isMyClockLow && isPlaying && !isGameOver && !readOnly) {
      if (!lowTimeFiredRef.current) {
        log.info('GameScreen', 'Own clock low crossed', { thresholdMs: lowMs, myClockMs });
        lowTimeFiredRef.current = true;
        vibrate(40);
        playSound('low-time');
      }
    } else if (isAboveLowRearm) {
      lowTimeFiredRef.current = false;
    }
  }, [isMyClockLow, isAboveLowRearm, isPlaying, isGameOver, readOnly]);

  useEffect(() => {
    if (isMyClockCritical && isPlaying && !isGameOver && !readOnly) {
      if (!criticalTimeFiredRef.current) {
        log.info('GameScreen', 'Own clock critical crossed', { thresholdMs: criticalMs, myClockMs });
        criticalTimeFiredRef.current = true;
        vibrate(40);
        playSound('low-time');
      }
    } else if (isAboveCriticalRearm) {
      criticalTimeFiredRef.current = false;
    }
  }, [isMyClockCritical, isAboveCriticalRearm, isPlaying, isGameOver, readOnly]);

  /* Nuovo oggetto a ogni tick, e va bene: lo leggono due foglie. Non si
     memoizza su `myClockMs`/`oppClockMs` perche' cambiano comunque ogni volta,
     e un `useMemo` qui sarebbe solo un confronto in piu' per lo stesso esito. */
  const valoriOrologio: ValoriOrologio = { mio: myClockMs, avversario: oppClockMs };

  /* `clockState` e `tickClock` NON scendono: sono i due che cambiano identita'
     quattro volte al secondo, ed e' tutto il punto di questo involucro. */
  const { clockState: _c, tickClock: _t, ...resto } = props;

  return (
    <OrologioContext.Provider value={valoriOrologio}>
      <GameScreenMemo
        {...resto}
        hasClock={hasClock}
        isMyClockActive={Boolean(isMyClockActive)}
        isOppClockActive={isOppClockActive}
        isMyClockLow={isMyClockLow}
        isOppClockLow={isOppClockLow}
      />
    </OrologioContext.Provider>
  );
}
